import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, type AuthUser } from "../lib/auth.tsx";
import { apiRequest, ApiError } from "../lib/api.ts";
import { KryptonMark, OizomEndorsement } from "../components/ui/Logo.tsx";
import { Button } from "../components/ui/Button.tsx";

interface LoginResponse {
  token: string;
  expires_in: number;
  user: AuthUser;
}

const INPUT =
  "w-full rounded-[9px] border border-line bg-bg px-[11px] py-2 text-[13px] text-text outline-none transition focus:border-brand1 focus:ring-2 focus:ring-brand1/25";

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location } | null)?.from?.pathname ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) return <Navigate to={from} replace />;

  // DEV-only: mint a mock session so the UI can be previewed without the API.
  // Stripped from production builds (import.meta.env.DEV is false there). The
  // token is a real-shaped JWT with a far-future exp so it survives reloads;
  // its signature is bogus, so any real API call will 401 (fine when the
  // backend is down — fetches just error and pages show empty states).
  function devLogin() {
    const b64url = (o: unknown) =>
      btoa(JSON.stringify(o)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    const email = "dev.admin@oizom.local";
    const token =
      `${b64url({ alg: "none", typ: "JWT" })}.` +
      `${b64url({ email, exp: Math.floor(Date.now() / 1000) + 31_536_000 })}.dev`;
    login(token, { email });
    navigate(from, { replace: true });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiRequest<LoginResponse>("/admin/auth/login", {
        method: "POST",
        body: { email, password },
        noAuthRedirect: true,
      });
      login(res.token, res.user);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else {
        setError("Login failed. Check the API connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      {/* Brand panel — Krypton up top, Oizom endorsement at the bottom. */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-14 text-white md:flex"
        style={{ background: "linear-gradient(150deg,#46B84F 0%,#2E8038 58%,#1C5B27 100%)" }}
      >
        <div className="relative z-10 flex items-center gap-3.5">
          <KryptonMark size={44} tone="inverted" />
          <div>
            <div className="text-[26px] font-extrabold tracking-tight">Krypton</div>
            <div className="text-[10px] uppercase tracking-[0.28em] opacity-75">
              Core · oizom web console
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <h2 className="mb-3 max-w-[16ch] text-balance text-[30px] font-bold leading-tight tracking-tight">
            Your IoT fleet, self-hosted and secure.
          </h2>
          <p className="max-w-[36ch] text-[14px] leading-relaxed opacity-90">
            Krypton Core is the on-prem console for your Oizom devices — monitoring,
            configuration, calibration and OTA, under your own roof.
          </p>
        </div>

        <div className="relative z-10">
          <OizomEndorsement />
          <div className="mt-3 text-[11px] opacity-60">
            An Oizom product · © Oizom Instruments · krypton.oizom.com
          </div>
        </div>

        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-36 -right-28 h-[420px] w-[420px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,255,255,.16), transparent 66%)" }}
        />
      </div>

      {/* Form */}
      <div className="flex items-center justify-center bg-page px-7 py-10">
        <div className="w-full max-w-[360px]">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-text3">
            Oizom web console
          </div>
          <h1 className="mb-1 text-[24px] font-bold tracking-tight text-text">Sign in</h1>
          <p className="mb-6 text-[13px] text-text2">Use your Krypton Core account to continue.</p>

          <form onSubmit={onSubmit} className="space-y-3.5">
            {error ? (
              <div className="rounded-[9px] border border-g3/40 bg-g3/10 px-3 py-2.5 text-[12.5px] text-g3">
                {error}
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text3">
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text3">
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={INPUT}
              />
            </div>

            <Button type="submit" variant="primary" size="md" disabled={submitting} className="mt-1.5 w-full">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {import.meta.env.DEV ? (
            <div className="mt-5 border-t border-dashed border-line2 pt-4">
              <div className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.13em] text-text3">
                Dev preview · no API
              </div>
              <button
                type="button"
                onClick={() => devLogin()}
                className="w-full rounded-[9px] border border-dashed border-line2 py-2 text-[12px] font-medium text-text2 transition hover:bg-soft"
              >
                Enter (dev admin)
              </button>
              <p className="mt-1.5 text-center text-[10.5px] text-text3">
                Bypasses login for UI preview — data pages need the backend running.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
